// Ambient chat context. Merges three sources so the agent always sees
// what the operator is currently looking at:
//
//   1. brandId — read from BrandContext (the sidebar's active brand).
//   2. URL params — matched against known route patterns
//      (/campaigns/:id, /product-ads, etc.) so a page doesn't have to
//      wire up any provider calls to contribute its ids.
//   3. Imperative pushes — a page that holds an entity in local state
//      (an open <AdDetailModal> holding an adId, the GenerateAds
//      wizard holding a stepwise productId) calls useSetAgentContext
//      to register while it's mounted; unregisters on unmount.
//
// Precedence: imperative pushes > URL params > BrandContext. This
// means a modal open on top of a route can override the route's own
// id if the modal represents a more specific selection.
//
// The <AgentChat> component reads this context via useAgentContext()
// and forwards it to the SSE endpoint. Backend executors merge
// `context.<key>Id` into args for keys the LLM didn't supply — see
// server/services/agentTools.js dispatch() and
// server/docs/AGENT.md §"Adding a capability".

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useBrand } from '../brand/BrandContext';
import type { ChatContext } from './types';

// ── Public API ──────────────────────────────────────────────────────

type SetContextFn = (patch: Partial<ChatContext>) => void;
type ClearContextFn = (keys: (keyof ChatContext)[]) => void;

type AgentContextValue = {
  context:  ChatContext;
  // Imperative pushes — pages call setContext during mount and
  // clearContext during unmount. useSetAgentContext handles both.
  setContext: SetContextFn;
  clearContext: ClearContextFn;
};

const Ctx = createContext<AgentContextValue | null>(null);

/**
 * Push page-level context into the shared agent context for the
 * component's lifetime. Automatically unregisters on unmount so a
 * closed modal or navigated-away page doesn't leave stale ids
 * behind.
 *
 * Usage:
 *   useSetAgentContext({ adId: openAd?.adId });
 *
 * Passing `undefined` for a key skips that key (the previous value —
 * URL-derived or another page's push — wins).
 */
export function useSetAgentContext(patch: Partial<ChatContext>): void {
  const ctx = useContext(Ctx);
  const patchRef = useRef(patch);
  patchRef.current = patch;
  // The provider value is a new object whenever imperative state
  // changes. Depending on that object re-runs this effect, whose
  // cleanup clears the keys and whose body sets them again.
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  // Register on mount + on patch change; unregister on unmount.
  // Effect keys off a stringification so a stable patch object
  // doesn't cause re-registers. The keys removed on unmount are the
  // keys we set (not the merged set) — precise so we don't wipe
  // context another mounted page set. An empty patch registers
  // nothing and does not clear another page's keys.
  const key = JSON.stringify(patch);
  useEffect(() => {
    const live = ctxRef.current;
    if (!live) return;
    const clean = Object.fromEntries(
      Object.entries(patchRef.current).filter(([, v]) => typeof v === 'string' && v.length > 0)
    ) as Partial<ChatContext>;
    if (Object.keys(clean).length === 0) return;
    live.setContext(clean);
    return () => {
      live.clearContext(Object.keys(clean) as (keyof ChatContext)[]);
    };
  }, [key]);
}

/**
 * Read the current merged agent context. Safe to call outside a
 * provider — returns an empty object so the AgentChat can degrade
 * gracefully (e.g. if the app boots without wrapping).
 */
export function useAgentContext(): ChatContext {
  const ctx = useContext(Ctx);
  return ctx?.context || {};
}

// ── URL pattern matching ────────────────────────────────────────────
//
// Kept as an ordered list of { regex → keyExtractor } so a new route
// with an id is a one-line add. Non-matching pathnames contribute
// nothing (empty object).

type RouteMatcher = {
  regex: RegExp;
  extract: (m: RegExpMatchArray) => Partial<ChatContext>;
};

const ROUTE_MATCHERS: RouteMatcher[] = [
  {
    regex: /^\/campaigns\/([a-f0-9]{24})(?:\/|$)/i,
    extract: (m) => ({ campaignId: m[1] })
  },
  {
    regex: /^\/product-ads\/([a-f0-9]{24})(?:\/|$)/i,
    extract: (m) => ({ productId: m[1] })
  }
  // /ads/:id doesn't exist as a standalone route today; ad selection
  // is modal-based on the ProductAds page (see the demonstrator push
  // in that file). If a future /ads/:id route ships, add a matcher
  // here — no other code needs to change.
];

function contextFromPathname(pathname: string): Partial<ChatContext> {
  for (const { regex, extract } of ROUTE_MATCHERS) {
    const m = pathname.match(regex);
    if (m) return extract(m);
  }
  return {};
}

// ── Provider ────────────────────────────────────────────────────────

export function AgentContextProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { activeBrand } = useBrand();
  const [imperative, setImperative] = useState<Partial<ChatContext>>({});

  // URL-derived context. Recomputes on pathname change; independent
  // of imperative pushes so a modal open on one route doesn't
  // survive a route change.
  const urlContext = useMemo(() => contextFromPathname(location.pathname), [location.pathname]);

  // Merge, precedence: imperative > URL > brand.
  const merged: ChatContext = useMemo(() => {
    const out: ChatContext = {};
    if (activeBrand?.id) out.brandId = activeBrand.id;
    Object.assign(out, urlContext);
    Object.assign(out, imperative);
    return out;
  }, [activeBrand?.id, urlContext, imperative]);

  const setContext = useCallback<SetContextFn>((patch) => {
    setImperative((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [k, v] of Object.entries(patch)) {
        if (typeof v === 'string' && v.length > 0 && (prev as Record<string, unknown>)[k] !== v) {
          (next as Record<string, string>)[k] = v;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  const clearContext = useCallback<ClearContextFn>((keys) => {
    setImperative((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const k of keys) {
        if (k in next) {
          delete next[k];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  const value = useMemo(() => ({ context: merged, setContext, clearContext }), [merged, setContext, clearContext]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
